import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaPersonnel } from "./useMediaPersonnel";
import { useAccreditationDemands } from "./useAccreditationDemands";
import { isActionableDemand } from "./accreditationDemandLogic";
import { useAccreditationRequests } from "./useAccreditationRequests";
import { useLeagueAccessRequests } from "./useLeagueAccess";
import { isPendingProfile } from "./mediaProfileReviewLogic";
import { useMediaOrganizations } from "./useMediaOrganizations";
import { usePVReportCheckPending } from "./usePVReportCheck";
import { useMotifAccumulationPending } from "./useMotifAccumulation";
import { useSharedOfficialNotices } from "./useSharedOfficialNotices";
import { usePendingModificationRequests } from "./useOfficialScaling";
import { usePendingProposalMatches } from "./useRefereeProposals";
import { useLeagueSeasonStore } from "../state/useLeagueSeasonStore";
import { buildOfficialNoticeItems, type PendingRequestLike } from "./officialNoticeItems";
import { getLocalizedPlayerName } from "../utils/nameHelpers";
import { useAuth } from "../contexts/AuthContext";
import { isSuperAdmin } from "../lib/auth/isSuperAdmin";
import { canSeeOfficialLinkNotices } from "../lib/auth/canSeeOfficialLinkNotices";
import { isNewerThanSeen, useNotificationSeenStore } from "../state/useNotificationSeenStore";
import { AccreditationStatus } from "../types/media";
import type { MediaPersonnel, AccreditationDemand } from "../types/media";

// ============================================================================
// Admin notification center — SINGLE SOURCE OF TRUTH
// ----------------------------------------------------------------------------
// This hook does NOT own any state or run a bespoke head-count query. It simply
// COMPOSES the existing media hooks with narrow server filters and derives a
// pending projection client-side. Because it reuses those hooks' exact query
// keys:
//   - useMediaPersonnel      -> ["media-personnel", currentLeagueId, filters]
//   - useAccreditationDemands-> ["accreditation-demands", currentLeagueId, currentSeason, filters]
// the existing verify/reject (useVerifyMediaPersonnel / useRejectMediaPersonnel)
// and review (useReviewAccreditationDemand) mutations already invalidate these
// keys on success. So when an item is "treated" its record's status changes,
// the query refetches, it drops out of the pending projection, and the badge
// decrements automatically — with NO mark-as-read write and NO new DB table.
//
// Live refresh therefore comes purely from react-query invalidation + the
// hooks' staleTime / window-focus refetch. There is intentionally NO
// realtime/websocket subscription here.
// ============================================================================

export type NotificationType =
    | "account"
    | "demand"
    | "request"
    | "league_access"
    | "organization"
    | "pv_report_check"
    | "motif_accumulation"
    | "official_link"
    | "official_request"
    | "referee_proposal";

export type NotificationCategory = "media" | "discipline" | "officials";

export const categoryOf = (type: NotificationType): NotificationCategory =>
    type === "pv_report_check" || type === "motif_accumulation"
        ? "discipline"
        : type === "official_link" || type === "official_request" || type === "referee_proposal"
            ? "officials"
            : "media";

export interface NotificationItem {
    /** Stable, collision-proof key: `${type}-${recordId}`. */
    id: string;
    type: NotificationType;
    /** Journalist name (account) or organization name (demand). */
    title: string;
    /** Contextual line: organization (account) or match teams / league (demand). */
    subtitle: string;
    /** ISO timestamp of the underlying record; used for relative-time + sort. */
    createdAt: string;
    /** In-app route the row navigates to when clicked. */
    route: string;
    /** No relative time: `createdAt` is a meeting date, not when the item appeared. */
    timeless?: boolean;
    /**
     * Nothing to treat (« officiel rattaché à une autre ligue »): listed while the
     * source returns it, but counted in the badge only while newer than the
     * user's last bell opening (state/useNotificationSeenStore).
     */
    informational?: boolean;
}

export interface AdminNotifications {
    totalPending: number;
    accountsPending: number;
    demandsPending: number;
    items: NotificationItem[];
    isLoading: boolean;
}

/**
 * Whether the current user may see the notification bell at all: media
 * reviewers (media items), disciplinary users (PVs with a report check to
 * treat) and the audience of the officials link notices (presidents, admins,
 * super-admin — canSeeOfficialLinkNotices). Each source stays gated by its own
 * rule inside the hook.
 */
export function useCanSeeNotifications(): boolean {
    const { permissions, user } = useAuth();
    return (
        permissions.can("view", "media") ||
        permissions.can("view", "disciplinary") ||
        permissions.can("validate", "assignment_proposal") ||
        canSeeOfficialLinkNotices(user)
    );
}

/**
 * Whether the current user may see the media notification center. Resolved from
 * the permission matrix ('media'), the same check used by the `/media` route
 * guard, the nav item and MediaContainer — one source, no drift.
 */
export function useIsMediaAdmin(): boolean {
    const { permissions } = useAuth();
    return permissions.can("view", "media");
}

const pickName = (
    isAr: boolean,
    name?: string | null,
    nameAr?: string | null
): string => (isAr ? nameAr || name : name || nameAr) || "";

/** Best-effort display name for a (possibly account-less, imported) journalist. */
function personName(p: MediaPersonnel, isAr: boolean): string {
    if (p.profile?.full_name) return p.profile.full_name;
    const fr = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
    const ar = [p.first_name_ar, p.last_name_ar].filter(Boolean).join(" ").trim();
    const chosen = isAr ? ar || fr : fr || ar;
    return chosen || p.email || "";
}

/** Meeting date (a calendar day, no time zone shift) in the UI language. */
function formatMeetingDate(date: string, isAr: boolean): string {
    const [y, m, d] = date.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return date;
    return new Intl.DateTimeFormat(isAr ? "ar-DZ" : "fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "Home - Away" (falls back to the league name) for a demand's match. */
function demandContext(d: AccreditationDemand, isAr: boolean): string {
    const m = d.match;
    const home = pickName(isAr, m?.home_team?.name, m?.home_team?.name_ar);
    const away = pickName(isAr, m?.away_team?.name, m?.away_team?.name_ar);
    if (home && away) return `${home} - ${away}`;
    return pickName(isAr, d.league?.name, d.league?.name_ar);
}

export function useAdminNotifications(): AdminNotifications {
    const { t, i18n } = useTranslation("notifications");
    const isAr = (i18n.language || "").toLowerCase().startsWith("ar");
    const isAdmin = useIsMediaAdmin();
    const { permissions, user } = useAuth();
    const canDiscipline = permissions.can("view", "disciplinary");
    // Same audience as get_shared_official_link_notices (privileged or president).
    const canOfficials = canSeeOfficialLinkNotices(user);
    const superAdmin = isSuperAdmin(user);
    const linksSeenAt = useNotificationSeenStore((s) =>
        user?.id ? s.officialLinksSeenAt[user.id] : undefined
    );
    // Officiels : rattachements à une autre ligue (présidents, super-admin) et
    // demandes d'officiels partagés (super-admin).
    const noticesQuery = useSharedOfficialNotices(canOfficials);
    const sharedRequestsQuery = usePendingModificationRequests(undefined, superAdmin);
    const noticesData = noticesQuery.data;
    const sharedRequestsData = sharedRequestsQuery.data;
    // Propositions d'arbitres en attente (président, responsable planification) : ligue/saison sélectionnées.
    const canProposals = permissions.can("validate", "assignment_proposal");
    const { currentLeagueId, currentSeason } = useLeagueSeasonStore();
    const proposalLeagueIds = useMemo(() => (currentLeagueId ? [currentLeagueId] : []), [currentLeagueId]);
    const proposalMatchesData = usePendingProposalMatches(proposalLeagueIds, currentSeason, canProposals).data;

    // Narrow server filters + admin gate. `enabled` also depends on the selected
    // league/season inside each hook, so the badge is league/season-scoped.
    const accountsQuery = useMediaPersonnel({
        filters: { isVerified: false },
        enabled: isAdmin,
    });
    const demandsQuery = useAccreditationDemands({
        filters: { status: "pending" },
        enabled: isAdmin,
    });
    // G5: standalone (non-demand) submitted requests were invisible in the bell.
    const requestsQuery = useAccreditationRequests({
        filters: { status: AccreditationStatus.SUBMITTED },
        enabled: isAdmin,
    });
    // Pending league-access requests (org_league_access) of the selected league.
    const leagueAccessQuery = useLeagueAccessRequests({
        filters: { status: "pending" },
        enabled: isAdmin,
    });
    // Fork intake: self-signed-up organizations awaiting the agrément.
    const orgsQuery = useMediaOrganizations(
        { isApproved: false, isActive: true },
        { enabled: isAdmin }
    );
    // Discipline: draft PVs whose FAF report check has findings to treat.
    const pvPendingQuery = usePVReportCheckPending(canDiscipline);
    // Discipline: suspensions created by a motif accumulation rule (Art. 71…)
    // not yet in a published PV, and thresholds that became caduc.
    const motifPendingQuery = useMotifAccumulationPending(canDiscipline);

    const accountsData = accountsQuery.data?.data;
    const demandsData = demandsQuery.data;
    const requestsData = requestsQuery.data?.data;
    const leagueAccessData = leagueAccessQuery.data;
    const orgsData = orgsQuery.data?.data;
    const pvPendingData = pvPendingQuery.data;
    const motifPendingData = motifPendingQuery.data;

    // Minute tick: a demand leaves the bell at kick-off even if no refetch
    // happens in between (same cadence as the media header and Aperçu).
    const [nowTick, setNowTick] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
        return () => window.clearInterval(id);
    }, []);

    return useMemo<AdminNotifications>(() => {
        // Client-derive the pending projection. `isVerified:false` also returns
        // rejected profiles and freelancers (disabled in the org-only model) —
        // isPendingProfile keeps only lirf_status 'pending' rows attached to an
        // organization, i.e. exactly what the Comptes tab lets a reviewer decide.
        const accountsPending = (accountsData ?? []).filter(isPendingProfile);
        // A pending demand whose match has kicked off can no longer be decided.
        const now = new Date(nowTick);
        const demandsPending = (demandsData ?? []).filter((d) =>
            isActionableDemand(d, now)
        );
        // Standalone only: demand children already surface via the demand group.
        const requestsPending = ((requestsData ?? []) as any[]).filter(
            (r) => !r.demand_id
        );
        const leagueAccessPending = leagueAccessData ?? [];
        const orgsPending = isAdmin ? (orgsData ?? []) : [];

        const accountItems: NotificationItem[] = accountsPending.map((p) => ({
            id: `account-${p.id}`,
            type: "account",
            title: personName(p, isAr) || t("item_account"),
            subtitle: pickName(isAr, p.organization?.name, p.organization?.name_ar),
            createdAt: p.created_at,
            route: `/media?tab=accounts&personnel=${p.id}`,
        }));

        const demandItems: NotificationItem[] = demandsPending.map((d) => ({
            id: `demand-${d.id}`,
            type: "demand",
            title:
                pickName(isAr, d.organization?.name, d.organization?.name_ar) ||
                t("item_demand"),
            subtitle: demandContext(d, isAr),
            createdAt: d.created_at,
            route: `/media?tab=demands&demand=${d.id}`,
        }));

        const requestItems: NotificationItem[] = requestsPending.map((r) => ({
            id: `request-${r.id}`,
            type: "request",
            title:
                (r.media_personnel ? personName(r.media_personnel, isAr) : "") ||
                t("item_request"),
            subtitle:
                r.organization_name ||
                pickName(
                    isAr,
                    r.media_personnel?.organization?.name,
                    r.media_personnel?.organization?.name_ar
                ),
            createdAt: r.created_at,
            route: `/media?tab=accreditations&accreditation=${r.id}`,
        }));

        // updated_at, not created_at: a re-request after a rejection reuses the row.
        const leagueAccessItems: NotificationItem[] = leagueAccessPending.map((a) => ({
            id: `league_access-${a.id}`,
            type: "league_access",
            title:
                pickName(isAr, a.organization?.name, a.organization?.name_ar) ||
                t("item_league_access"),
            subtitle: pickName(isAr, a.league?.name, a.league?.name_ar),
            createdAt: a.updated_at || a.created_at,
            route: "/media?tab=league_access",
        }));

        const orgItems: NotificationItem[] = orgsPending.map((o) => ({
            id: `organization-${o.id}`,
            type: "organization",
            title: pickName(isAr, o.name, o.name_ar) || t("item_organization"),
            subtitle: o.contact_email || o.website || "",
            createdAt: o.created_at,
            route: `/media?tab=organizations&org=${o.id}`,
        }));

        const pvItems: NotificationItem[] = (canDiscipline ? pvPendingData ?? [] : []).map((p) => {
            const counts = [
                p.duplicates > 0 ? t("item_pv_duplicates", { count: p.duplicates }) : null,
                p.alerts > 0 ? t("item_pv_alerts", { count: p.alerts }) : null,
                p.attach > 0 ? t("item_pv_attach", { count: p.attach }) : null,
            ].filter(Boolean);
            const pv = p.publicationNumber
                ? t("item_pv_number", { number: p.publicationNumber })
                : t("item_pv_draft");
            return {
                id: `pv_report_check-${p.sessionId}`,
                type: "pv_report_check",
                title: [pv, counts.join(" · ")].join(" — "),
                subtitle: [p.committeeName, p.meetingDate ? formatMeetingDate(p.meetingDate, isAr) : null]
                    .filter(Boolean)
                    .join(" · "),
                createdAt: p.meetingDate ?? "",
                route: `/disciplinary/sessions/${p.sessionId}/pv?controle=1`,
                timeless: true,
            };
        });

        const motifItems: NotificationItem[] = (canDiscipline ? motifPendingData ?? [] : []).flatMap(({ row, rule }) => {
            const title = getLocalizedPlayerName(row.player, isAr ? "arabic" : "french");
            const lastCard = row.countedCards[row.countedCards.length - 1]?.decisionDate ?? "";
            const base = { type: "motif_accumulation" as const, title, createdAt: lastCard, route: "/disciplinary/audit/accumulations", timeless: true };
            if (row.caduc > 0) {
                return [{ ...base, id: `motif_accumulation-caduc-${row.ruleId}-${row.playerId}`, subtitle: t("item_motif_caduc", { article: rule.articleCode }) }];
            }
            return row.issued
                .filter((i) => !i.published)
                .map((i) => ({
                    ...base,
                    id: `motif_accumulation-${i.sanctionId}`,
                    subtitle: t("item_motif_created", { article: rule.articleCode, matches: rule.suspensionMatches, threshold: i.threshold }),
                }));
        });

        const officialItems = buildOfficialNoticeItems({
            notices: canOfficials ? noticesData ?? [] : [],
            // `requires_super_admin` comes from the `*` row (absent before Task 2).
            pendingRequests: superAdmin ? ((sharedRequestsData ?? []) as unknown as PendingRequestLike[]) : [],
            isSuperAdmin: superAdmin,
            mode: isAr ? "arabic" : "french",
            t,
        });

        const proposalItems: NotificationItem[] = (canProposals ? proposalMatchesData ?? [] : []).map((p) => {
            const home = pickName(isAr, p.home?.name, p.home?.name_ar);
            const away = pickName(isAr, p.away?.name, p.away?.name_ar);
            return {
                id: `referee_proposal-${p.matchId}`,
                type: "referee_proposal",
                title: t("item_referee_proposals", { count: p.count }),
                subtitle: [home && away ? `${home} - ${away}` : null, p.matchDate].filter(Boolean).join(" · "),
                createdAt: p.latestAt,
                route: "/planning",
            };
        });

        const items = [
            ...proposalItems,
            ...officialItems,
            ...pvItems,
            ...motifItems,
            ...accountItems,
            ...demandItems,
            ...requestItems,
            ...leagueAccessItems,
            ...orgItems,
        ].sort(
            (a, b) =>
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        // Informational items stop counting once the bell has been opened after them.
        const totalPending = items.filter(
            (i) => !i.informational || isNewerThanSeen(i.createdAt, linksSeenAt)
        ).length;

        return {
            totalPending,
            accountsPending: accountsPending.length,
            demandsPending: demandsPending.length,
            items,
            isLoading: accountsQuery.isLoading || demandsQuery.isLoading,
        };
    }, [
        accountsData,
        demandsData,
        nowTick,
        requestsData,
        leagueAccessData,
        orgsData,
        pvPendingData,
        motifPendingData,
        noticesData,
        sharedRequestsData,
        proposalMatchesData,
        canProposals,
        isAdmin,
        canDiscipline,
        canOfficials,
        superAdmin,
        linksSeenAt,
        isAr,
        t,
        accountsQuery.isLoading,
        demandsQuery.isLoading,
    ]);
}
